/**
 * Inventory Cular - Line Indicator for Stock Levels
 * Shows horizontal lines that represent current inventory levels
 */

class InventoryCular {
  constructor(element) {
    this.container = element;
    this.variantId = element.dataset.variantId;
    this.inventoryQuantity = parseInt(element.dataset.inventoryQuantity) || 0;
    this.inventoryManagement = element.dataset.inventoryManagement;
    this.inventoryThreshold = parseInt(element.dataset.inventoryThreshold) || 10;
    this.maxLines = parseInt(element.dataset.maxLines) || 10;
    this.showQuantity = element.dataset.showInventoryQuantity !== 'false';
    
    this.linesContainer = element.querySelector('.inventory-cular__lines');
    this.statusText = element.querySelector('.inventory-cular__text');
    
    this.init();
  }

  init() {
    console.log('InventoryCular init:', {
      inventoryQuantity: this.inventoryQuantity,
      inventoryManagement: this.inventoryManagement,
      maxLines: this.maxLines,
      linesContainer: this.linesContainer
    });
    
    this.createLines();
    this.updateDisplay();
    this.bindEvents();
  }

  createLines() {
    if (!this.linesContainer) {
      console.error('Lines container not found');
      return;
    }
    
    // Don't clear existing lines - just add to them or update them
    const existingLines = this.linesContainer.querySelectorAll('.inventory-cular__line');
    
    // If we already have lines, don't recreate them
    if (existingLines.length >= this.maxLines) {
      console.log('Lines already exist, not recreating');
      return;
    }
    
    // Clear and create new lines with explicit styling
    this.linesContainer.innerHTML = '';
    
    for (let i = 0; i < this.maxLines; i++) {
      const line = document.createElement('div');
      line.className = 'inventory-cular__line';
      line.dataset.index = i;
      
      // Add explicit inline styles to ensure visibility
      line.style.display = 'block';
      line.style.backgroundColor = 'var(--line-color-empty)';
      
      this.linesContainer.appendChild(line);
    }
    
    console.log('Created', this.maxLines, 'lines with explicit styling');
  }

  updateLines() {
    if (!this.linesContainer) return;
    
    const lines = this.linesContainer.querySelectorAll('.inventory-cular__line');
    let filledLines = 0;
    
    if (this.inventoryManagement === 'shopify' && this.inventoryQuantity >= 0) {
      // Calculate filled lines based on current inventory
      if (this.inventoryQuantity === 0) {
        filledLines = 0;
      } else if (this.inventoryQuantity <= this.inventoryThreshold) {
        // Low stock: show 1-3 lines based on how low
        const lowRatio = this.inventoryQuantity / this.inventoryThreshold;
        filledLines = Math.max(1, Math.ceil(lowRatio * 3));
      } else {
        // Normal stock: scale based on reasonable max (100 items = full)
        const maxStock = 100;
        const ratio = Math.min(this.inventoryQuantity / maxStock, 1);
        filledLines = Math.max(3, Math.ceil(ratio * this.maxLines));
      }
    } else {
      // No inventory tracking - show all lines
      filledLines = this.maxLines;
    }
    
    console.log('Updating lines:', {
      totalLines: lines.length,
      filledLines: filledLines,
      inventoryQuantity: this.inventoryQuantity
    });
    
    // Animate lines with staggered timing
    this.linesContainer.dataset.animating = 'true';
    
    lines.forEach((line, index) => {
      line.classList.remove('inventory-cular__line--filled', 'inventory-cular__line--empty', 'inventory-cular__line--filling');
      
      setTimeout(() => {
        if (index < filledLines) {
          line.classList.add('inventory-cular__line--filled', 'inventory-cular__line--filling');
          // Use CSS variable for color
          line.style.backgroundColor = 'var(--line-color)';
        } else {
          line.classList.add('inventory-cular__line--empty');
          // Use CSS variable for empty color
          line.style.backgroundColor = 'var(--line-color-empty)';
        }
      }, index * 30); // Slightly faster animation
    });
    
    // Remove animation flag after completion
    setTimeout(() => {
      this.linesContainer.dataset.animating = 'false';
      lines.forEach(line => line.classList.remove('inventory-cular__line--filling'));
    }, this.maxLines * 50 + 300);
  }

  updateStockLevel() {
    let stockLevel = 'high';
    
    if (this.inventoryManagement === 'shopify' && this.inventoryQuantity >= 0) {
      if (this.inventoryQuantity === 0) {
        stockLevel = 'out';
      } else if (this.inventoryQuantity <= 3) {
        stockLevel = 'critical';
      } else if (this.inventoryQuantity <= this.inventoryThreshold) {
        stockLevel = 'low';
      } else if (this.inventoryQuantity <= 25) {
        stockLevel = 'medium';
      } else {
        stockLevel = 'high';
      }
    }
    
    this.container.dataset.stockLevel = stockLevel;
  }

  updateStatusText() {
    if (this.statusText) {
      if (this.inventoryManagement === 'shopify') {
        if (this.inventoryQuantity > 0) {
          if (this.showQuantity) {
            this.statusText.textContent = `${this.inventoryQuantity} in stock`;
          } else {
            this.statusText.textContent = 'In stock';
          }
        } else {
          this.statusText.textContent = 'Out of stock';
        }
      } else {
        this.statusText.textContent = 'Available';
      }
    }
  }

  updateDisplay() {
    this.updateStatusText();
    this.updateStockLevel();
    this.updateLines();
  }

  bindEvents() {
    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.variantId = event.detail.variant.id;
        this.inventoryQuantity = event.detail.variant.inventory_quantity || 0;
        this.inventoryManagement = event.detail.variant.inventory_management;
        
        // Update data attributes
        this.container.dataset.variantId = this.variantId;
        this.container.dataset.inventoryQuantity = this.inventoryQuantity;
        this.container.dataset.inventoryManagement = this.inventoryManagement;
        
        this.updateDisplay();
      }
    });

    // Handle quantity selector changes (if present on the page)
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="quantity"]')) {
        // Optionally update display based on selected quantity
        // For now, we just show total inventory regardless of selected quantity
        this.updateDisplay();
      }
    });
  }
}

// Initialize all inventory cular indicators
document.addEventListener('DOMContentLoaded', () => {
  const inventoryCulars = document.querySelectorAll('.inventory-cular');
  inventoryCulars.forEach(element => {
    new InventoryCular(element);
  });
});

// Handle dynamic section loading
document.addEventListener('shopify:section:load', (event) => {
  const inventoryCulars = event.target.querySelectorAll('.inventory-cular');
  inventoryCulars.forEach(element => {
    new InventoryCular(element);
  });
});

// Handle section reloading
document.addEventListener('shopify:section:reorder', () => {
  const inventoryCulars = document.querySelectorAll('.inventory-cular');
  inventoryCulars.forEach(element => {
    new InventoryCular(element);
  });
});
